import React, { useState, useEffect, useRef } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { MOCK_STOCKS_WITH_HISTORY } from '../data/mockStocks';
import './StockHistory.css';

const StockHistory: React.FC = () => {
  const { state } = usePortfolio();
  const [selectedStock, setSelectedStock] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [displayedHistory, setDisplayedHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Use state to avoid unused variable warning
  console.log('Portfolio state:', state);

  const selectedStockData = MOCK_STOCKS_WITH_HISTORY.find(stock => stock.ticker === selectedStock);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // formatDateTime function removed as it was unused

  // Initialize with first stock and recent history
  useEffect(() => {
    if (MOCK_STOCKS_WITH_HISTORY.length > 0 && !selectedStock) {
      const firstStock = MOCK_STOCKS_WITH_HISTORY[0];
      setSelectedStock(firstStock.ticker);
      setSelectedDate('');
      
      // Show last 30 days of data
      const recentHistory = firstStock.history.slice(-30);
      setDisplayedHistory(recentHistory);
    }
  }, [selectedStock]);

  // Update displayed history when stock or date changes
  useEffect(() => {
    if (selectedStockData) {
      let history = [...selectedStockData.history];
      
      if (selectedDate) {
        const targetDate = new Date(selectedDate);
        const targetIndex = history.findIndex(point => 
          point.date.toDateString() === targetDate.toDateString()
        );
        
        if (targetIndex !== -1) {
          // Show 15 days before and after the selected date
          const startIndex = Math.max(0, targetIndex - 15);
          const endIndex = Math.min(history.length, targetIndex + 16);
          history = history.slice(startIndex, endIndex);
        }
      } else {
        // Show last 30 days
        history = history.slice(-30);
      }
      
      setDisplayedHistory(history);
      setHasMore(history.length < selectedStockData.history.length);
    }
  }, [selectedStock, selectedDate, selectedStockData]);

  const loadMoreHistory = () => {
    if (!selectedStockData || isLoading) return;
    
    setIsLoading(true);
    
    // Simulate loading delay
    setTimeout(() => {
      const currentLength = displayedHistory.length;
      const newHistory = selectedStockData.history.slice(0, currentLength + 20);
      
      setDisplayedHistory(newHistory);
      setHasMore(newHistory.length < selectedStockData.history.length);
      setIsLoading(false);
    }, 500);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !isLoading) {
      loadMoreHistory();
    }
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const resetToRecent = () => {
    setSelectedDate('');
  };

  const getPriceChange = (currentPrice: number, previousPrice: number) => {
    const change = currentPrice - previousPrice;
    const changePercent = (change / previousPrice) * 100;
    return { change, changePercent };
  };

  return (
    <div className="stock-history">
      <div className="history-header">
        <h3>Stock Price History</h3>
        <div className="history-controls">
          <select
            value={selectedStock}
            onChange={(e) => {
              setSelectedStock(e.target.value);
              setSelectedDate('');
            }}
            className="stock-select"
          >
            {MOCK_STOCKS_WITH_HISTORY.map(stock => (
              <option key={stock.ticker} value={stock.ticker}>
                {stock.ticker} - {stock.name}
              </option>
            ))}
          </select>
          
          <div className="date-controls">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="date-input"
              placeholder="Jump to date"
            />
            <button
              onClick={resetToRecent}
              className="reset-btn"
              disabled={!selectedDate}
            >
              Recent
            </button>
          </div>
        </div>
      </div>

      {selectedStockData && (
        <div className="history-content">
          <div className="stock-summary">
            <div className="summary-info">
              <h4>{selectedStockData.ticker}</h4>
              <p>{selectedStockData.name}</p>
            </div>
            <div className="summary-price">
              <span className="current-price">
                {formatCurrency(selectedStockData.currentPrice)}
              </span>
              <span className={`price-change ${selectedStockData.change >= 0 ? 'positive' : 'negative'}`}>
                {selectedStockData.change >= 0 ? '+' : ''}{formatCurrency(selectedStockData.change)} 
                ({selectedStockData.changePercent >= 0 ? '+' : ''}{selectedStockData.changePercent.toFixed(2)}%)
              </span>
            </div>
          </div>

          <div className="history-chart">
            <div className="chart-placeholder">
              <div className="chart-icon">📈</div>
              <p>Price Chart</p>
              <small>TODO: Implement interactive chart with Chart.js</small>
            </div>
          </div>

          <div className="history-list">
            <div className="list-header">
              <span>Date</span>
              <span>Price</span>
              <span>Change</span>
            </div>
            
            <div 
              className="list-content"
              ref={scrollContainerRef}
              onScroll={handleScroll}
            >
              {displayedHistory.map((point, index) => {
                const previousPoint = index > 0 ? displayedHistory[index - 1] : null;
                const priceChange = previousPoint ? getPriceChange(point.price, previousPoint.price) : null;
                
                return (
                  <div key={point.date.getTime()} className="history-item">
                    <div className="item-date">
                      {formatDate(point.date)}
                    </div>
                    <div className="item-price">
                      {formatCurrency(point.price)}
                    </div>
                    <div className={`item-change ${priceChange && priceChange.change >= 0 ? 'positive' : 'negative'}`}>
                      {priceChange ? (
                        <>
                          {priceChange.change >= 0 ? '+' : ''}{formatCurrency(priceChange.change)}
                          <br />
                          <small>({priceChange.changePercent >= 0 ? '+' : ''}{priceChange.changePercent.toFixed(2)}%)</small>
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                );
              })}
              
              {isLoading && (
                <div className="loading-item">
                  <div className="loading-spinner"></div>
                  <span>Loading more data...</span>
                </div>
              )}
              
              {!hasMore && displayedHistory.length > 0 && (
                <div className="end-item">
                  <span>End of data</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockHistory;
